import {
  type ReactNode,
  type Ref,
  forwardRef,
  type CSSProperties,
} from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

interface CheckboxProps {
  className?: string;
  inputClassName?: string;
  // Doesn't support tailwind class names (e.g. cosmic, alien, etc.)
  checkboxColor?: string;
  isDisabled?: boolean;
  label?: ReactNode;
  labelClassName?: string;
  onChange?: (value: boolean) => void;
  subLabel?: ReactNode;
  value: boolean;
  size?: "sm" | "md" | "lg";
}

interface CustomCss extends CSSProperties {
  "--checkbox-color": string;
}

export const Checkbox = forwardRef(
  (
    {
      label,
      subLabel,
      value,
      onChange,
      className,
      inputClassName,
      checkboxColor,
      labelClassName,
      isDisabled = false,
      size = "md",
    }: CheckboxProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const toggleChecked = () => !isDisabled && onChange?.(!value);

    return (
      <span
        className={twMerge(
          "inline-flex items-center",
          clsx({
            "cursor-not-allowed ": isDisabled,
            "cursor-pointer": !isDisabled,
          }),
          className,
        )}
        onClick={toggleChecked}
      >
        <input
          ref={ref}
          className={clsx(
            "[-moz-appearance:initial] cursor-[inherit]",
            {
              "pointer-events-none": isDisabled,
            },
            {
              "w-3 h-3 before:w-3 before:h-3": size === "sm",
              "w-4 h-4 before:w-4 before:h-4": size === "md",
              "w-5 h-5 before:w-5 before:h-5": size === "lg",
            },
            inputClassName,
          )}
          disabled={isDisabled}
          type="checkbox"
          checked={value}
          style={
            {
              "--checkbox-color": checkboxColor,

              ...(isDisabled && { "--checkbox-color": "#9CA3AF" }),
            } as CustomCss
          }
          readOnly
        />
        <div className="flex flex-col flex-grow ml-2 cursor-[inherit]">
          {label && (
            <label className={clsx("my-0.5 cursor-[inherit]", labelClassName)}>
              {label}
            </label>
          )}
          {subLabel}
        </div>
      </span>
    );
  },
);
